/**
 * LegalDocumentModal — read-only viewer for a candidate-facing legal
 * document (Terms of Service, Privacy Policy), opened from the consent
 * screen's inline links.
 *
 * Renders markdown through the existing MarkdownRenderer rather than
 * hand-rolled markup, so these documents pick up the same typography as
 * every other rendered document in the app, and editing the legal text
 * never means editing JSX.
 *
 * Deliberately has no dependency on auth/session context: it is shown on
 * the unauthenticated candidate flow, where the URL token is the only
 * credential.
 */

import React, { useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

import MarkdownRenderer from './MarkdownRenderer'

interface LegalDocumentModalProps {
  open: boolean
  title: string
  content: string
  onClose: () => void
}

export default function LegalDocumentModal({ open, title, content, onClose }: LegalDocumentModalProps) {
  // Esc closes. Bound only while open so this never competes with other
  // key handling on the interview screens.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Stops a click inside the panel from reaching the backdrop's close handler. */}
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          <MarkdownRenderer content={content} />
        </div>
      </div>
    </div>
  )
}
